import { useEffect, useState } from 'react';
import { 
  Gift, Plus, Search, Trash2, Edit, Calendar,
  Package, CheckCircle, Tag
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OFFER_TYPES = [
  { value: 'buy_x_get_y', label: 'Buy X Get Y Free', description: 'Buy 2 Get 1 Free type offers' },
  { value: 'buy_2_get_3', label: 'Buy 2 Get 3', description: 'Buy 2 items, get 3rd free' },
  { value: 'combo_discount', label: 'Combo Discount', description: 'Discount on buying multiple items' }
];

export const AdminBundleOffers = () => {
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    type: 'buy_x_get_y',
    buy_quantity: 2,
    get_quantity: 1,
    discount_on_get: 100,
    applicable_products: [],
    applicable_categories: [],
    max_uses_per_order: 1,
    valid_from: '',
    valid_until: '',
    is_active: true,
    description: '',
    badge_text: ''
  });

  useEffect(() => {
    loadOffers();
    loadCategoriesAndProducts();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/bundle-offers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOffers(response.data.offers || []);
    } catch (error) {
      console.error('Error loading bundle offers:', error);
      toast.error('Failed to load bundle offers');
    } finally {
      setLoading(false);
    }
  };

  const loadCategoriesAndProducts = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCategories(catRes.data.categories || []);
      setProducts(prodRes.data.products || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.valid_from || !formData.valid_until) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        valid_from: new Date(formData.valid_from).toISOString(),
        valid_until: new Date(formData.valid_until).toISOString()
      };

      if (editingOffer) {
        await axios.put(`${API}/admin/bundle-offers/${editingOffer.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bundle offer updated successfully');
      } else {
        await axios.post(`${API}/admin/bundle-offers`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Bundle offer created successfully');
      }

      setShowModal(false);
      resetForm();
      loadOffers();
    } catch (error) {
      console.error('Error saving bundle offer:', error);
      toast.error(error.response?.data?.detail || 'Failed to save bundle offer');
    }
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/bundle-offers/${offerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bundle offer deleted');
      loadOffers();
    } catch (error) {
      console.error('Error deleting bundle offer:', error);
      toast.error('Failed to delete bundle offer');
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title,
      type: offer.type,
      buy_quantity: offer.buy_quantity,
      get_quantity: offer.get_quantity,
      discount_on_get: offer.discount_on_get,
      applicable_products: offer.applicable_products || [],
      applicable_categories: offer.applicable_categories || [],
      max_uses_per_order: offer.max_uses_per_order || 1,
      valid_from: offer.valid_from ? new Date(offer.valid_from).toISOString().slice(0, 16) : '',
      valid_until: offer.valid_until ? new Date(offer.valid_until).toISOString().slice(0, 16) : '',
      is_active: offer.is_active,
      description: offer.description || '',
      badge_text: offer.badge_text || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingOffer(null);
    setFormData({
      title: '',
      type: 'buy_x_get_y',
      buy_quantity: 2,
      get_quantity: 1,
      discount_on_get: 100,
      applicable_products: [],
      applicable_categories: [],
      max_uses_per_order: 1,
      valid_from: '',
      valid_until: '',
      is_active: true,
      description: '',
      badge_text: ''
    });
  };

  const getOfferStatus = (offer) => {
    const now = new Date();
    const validFrom = new Date(offer.valid_from);
    const validUntil = new Date(offer.valid_until);

    if (!offer.is_active) return { label: 'Inactive', color: 'bg-gray-100 text-gray-700' };
    if (now < validFrom) return { label: 'Upcoming', color: 'bg-blue-100 text-blue-700' };
    if (now > validUntil) return { label: 'Expired', color: 'bg-red-100 text-red-700' };
    return { label: 'Active', color: 'bg-green-100 text-green-700' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getOfferDescription = (offer) => {
    if (offer.type === 'buy_x_get_y') {
      const freeOrDiscount = offer.discount_on_get === 100 ? 'FREE' : `${offer.discount_on_get}% OFF`;
      return `Buy ${offer.buy_quantity}, Get ${offer.get_quantity} ${freeOrDiscount}`;
    }
    if (offer.type === 'buy_2_get_3') {
      return `Buy 2 Get 3 (${offer.discount_on_get}% off on 3rd)`;
    }
    return `${offer.discount_on_get}% off on ${offer.buy_quantity}+ items`;
  };

  return (
    <div className="space-y-6" data-testid="admin-bundle-offers">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-light text-charcoal flex items-center gap-2">
            <Gift className="w-6 h-6 text-purple-500" />
            Bundle Offers
          </h1>
          <p className="text-charcoal-light mt-1">Create Buy X Get Y and combo deals</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-gold text-white hover:bg-gold-dark transition-colors"
          data-testid="create-bundle-offer-btn"
        >
          <Plus className="w-4 h-4" /> Create Bundle Offer
        </button>
      </div>

      {/* Offers List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : offers.length === 0 ? (
        <div className="bg-white border border-charcoal/10 p-12 text-center">
          <Gift className="w-12 h-12 mx-auto text-charcoal-light mb-3" />
          <p className="text-charcoal-light">No bundle offers created yet</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="mt-4 text-gold hover:underline"
          >
            Create your first bundle offer
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {offers.map((offer) => {
            const status = getOfferStatus(offer);
            
            return (
              <div 
                key={offer.id}
                className="bg-white border border-charcoal/10 p-4 hover:border-gold/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-100 flex items-center justify-center">
                      <Gift className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-heading text-lg text-charcoal">{offer.title}</h3>
                        <span className={`px-2 py-0.5 text-xs ${status.color}`}>{status.label}</span>
                      </div>
                      <p className="text-sm font-medium text-purple-600 mb-1">
                        {getOfferDescription(offer)}
                      </p>
                      <p className="text-xs text-charcoal-light">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {formatDate(offer.valid_from)} - {formatDate(offer.valid_until)}
                      </p>
                      {offer.badge_text && (
                        <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-gold/20 text-gold">
                          <Tag className="w-3 h-3 inline mr-1" />
                          {offer.badge_text}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-lg font-heading text-charcoal">
                        {offer.applicable_categories?.length || offer.applicable_products?.length || 'All'}
                      </p>
                      <p className="text-xs text-charcoal-light">
                        {offer.applicable_categories?.length ? 'Categories' : 
                         offer.applicable_products?.length ? 'Products' : 'Products'}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(offer)}
                        className="p-2 hover:bg-beige transition-colors"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4 text-charcoal-light" />
                      </button>
                      <button
                        onClick={() => handleDelete(offer.id)}
                        className="p-2 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-ivory w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-charcoal/10">
              <h2 className="text-xl font-heading text-charcoal">
                {editingOffer ? 'Edit Bundle Offer' : 'Create Bundle Offer'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Offer Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Buy 2 Get 1 Free"
                  className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  required
                />
              </div>

              {/* Offer Type */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Offer Type *</label>
                <div className="grid gap-3">
                  {OFFER_TYPES.map((type) => (
                    <label
                      key={type.value}
                      className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                        formData.type === type.value
                          ? 'border-gold bg-gold/5'
                          : 'border-charcoal/20 hover:border-gold/30'
                      }`}
                    >
                      <input
                        type="radio"
                        name="offerType"
                        value={type.value}
                        checked={formData.type === type.value}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        className="mt-1"
                      />
                      <div>
                        <p className="font-medium text-charcoal">{type.label}</p>
                        <p className="text-xs text-charcoal-light">{type.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Quantities */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Buy Quantity *</label>
                  <input
                    type="number"
                    value={formData.buy_quantity}
                    onChange={(e) => setFormData({ ...formData, buy_quantity: parseInt(e.target.value) || 1 })}
                    min="1"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Get Quantity</label>
                  <input
                    type="number"
                    value={formData.get_quantity}
                    onChange={(e) => setFormData({ ...formData, get_quantity: parseInt(e.target.value) || 0 })}
                    min="0"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Discount % on Get</label>
                  <input
                    type="number"
                    value={formData.discount_on_get}
                    onChange={(e) => setFormData({ ...formData, discount_on_get: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                  <p className="text-xs text-charcoal-light mt-1">100 = Free</p>
                </div>
              </div>

              {/* Validity Period */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Valid From *</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Valid Until *</label>
                  <input
                    type="datetime-local"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                    required
                  />
                </div>
              </div>

              {/* Applicable Categories */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">
                  Applicable Categories (leave empty for all)
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-charcoal/10 p-3">
                  {categories.map((cat) => (
                    <label key={cat.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={formData.applicable_categories.includes(cat.id)}
                        onChange={(e) => {
                          const ids = e.target.checked
                            ? [...formData.applicable_categories, cat.id]
                            : formData.applicable_categories.filter(id => id !== cat.id);
                          setFormData({ ...formData, applicable_categories: ids });
                        }}
                        className="rounded"
                      />
                      {cat.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Badge Text */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Badge Text (for products)</label>
                  <input
                    type="text"
                    value={formData.badge_text}
                    onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
                    placeholder="Buy 2 Get 1"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Max Uses Per Order</label>
                  <input
                    type="number"
                    value={formData.max_uses_per_order}
                    onChange={(e) => setFormData({ ...formData, max_uses_per_order: parseInt(e.target.value) || 1 })}
                    min="1"
                    className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Promotional offer for festive season..."
                  className="w-full px-4 py-2 border border-charcoal/20 bg-white text-charcoal focus:border-gold focus:outline-none"
                />
              </div>

              {/* Active Status */}
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 rounded border-charcoal/20 text-gold focus:ring-gold"
                />
                <span className="text-sm text-charcoal">Active (offer can be applied)</span>
              </label>

              {/* Actions */}
              <div className="flex gap-4 pt-4 border-t border-charcoal/10">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-charcoal/20 text-charcoal hover:bg-beige"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gold text-white hover:bg-gold-dark"
                >
                  {editingOffer ? 'Update Offer' : 'Create Offer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBundleOffers;
